import { Backdrop } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Recommender, Talent } from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentRecommenders,
} from "../../../talents/talentsSlice";
import { UpdateTalentRecommenders } from "../../../talents/types/commands";
import { EditMode } from "../../TalentDetails";
import RecommenderForm from "./RecommenderForm";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type Props = {
  editMode: EditMode;
  onClose: () => void;
};

type FormValues = {
  name: string;
  title: string;
  company: string;
  phone: string;
  email: string;
  additionalInfo: string;
};

const TalentEditRecommender = ({ editMode, onClose }: Props) => {
  const { t } = useTranslation();

  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecommender] = useState<Recommender>(
    talent.recommenders?.values.find(
      (recommender) => recommender.id === editMode.id,
    ) as Recommender,
  );

  const defaultValues: FormValues = {
    name: selectedRecommender.name,
    title: selectedRecommender.title,
    company: selectedRecommender.company,
    phone: selectedRecommender.phone,
    email: selectedRecommender.email,
    additionalInfo: selectedRecommender.additionalInfo,
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const onSubmit = (formValues: FormValues) => {
    setIsLoading(true);

    if (talentId) {
      const existingValues = talent.recommenders?.values || [];
      const existingVersion = talent.recommenders?.version || 0;

      const updatedRecommenders: Recommender[] = existingValues.map(
        (recommender) => {
          if (recommender.id !== editMode.id) return recommender;

          return {
            id: recommender.id,
            name: formValues.name,
            title: formValues.title,
            company: formValues.company,
            phone: formValues.phone,
            email: formValues.email,
            additionalInfo: formValues.additionalInfo,
          };
        },
      );

      const commandPayload: UpdateTalentRecommenders = {
        talentId: talent.id,
        version: existingVersion,
        recommenders: updatedRecommenders,
      };

      dispatch(updateTalentRecommenders(commandPayload)).then((action) => {
        setIsLoading(false);
        onClose();
        return isFulfilled(action);
      });
    }
  };

  const onSave = () => handleSubmit(onSubmit)();

  const onCancel = () => onClose();

  const saveDisabled = !isEmpty(errors) || !isDirty || isLoading;

  const title = t("recommenders:recommender");

  return (
    <DialogContentWrapper
      title={title}
      margin
      saveDisabled={saveDisabled}
      onSave={onSave}
      onCancel={onCancel}
    >
      <Backdrop
        open={isLoading}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          zIndex: 1000,
        }}
      >
        <LoadingSpinner />
      </Backdrop>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <RecommenderForm />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default TalentEditRecommender;
